import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ArrowUpRight, Menu, MessageCircle, X } from "lucide-react";
import { WHATSAPP_URL } from "@/lib/contact";

const nav = [
  ["Home", "/"], ["Wealth Management", "/wealth-management"],
  ["Investment Solutions", "/investment-solutions"], ["Insights", "/insights"],
  ["About Us", "/about"], ["Contact", "/contact"],
];

export const Brand = ({ light = false }) => (
  <Link to="/" className={`brand ${light ? "brand--light" : ""}`} data-testid="brand-home-link" aria-label="Baaz Capital home">
    <img src="/assets/baaz-logo.png" alt="Baaz Capital circular emblem" width="40" height="40" />
    <span><strong>BAAZ</strong><small>CAPITAL</small></span>
  </Link>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const root = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY;
    const previous = {
      rootOverflow: root.style.overflow,
      bodyOverflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
    };
    root.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    return () => {
      root.style.overflow = previous.rootOverflow;
      body.style.overflow = previous.bodyOverflow;
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.left = previous.left;
      body.style.right = previous.right;
      body.style.width = previous.width;
      window.scrollTo(0, scrollY);
    };
  }, [open]);
  useEffect(() => { const handler = () => setScrolled(window.scrollY > 24); handler(); window.addEventListener("scroll", handler); return () => window.removeEventListener("scroll", handler); }, []);
  return (
    <header className={`site-header ${scrolled ? "is-scrolled" : ""}`} data-testid="site-header">
      <div className="header-inner">
        <Brand />
        <nav className="desktop-nav" aria-label="Primary navigation" data-testid="desktop-navigation">
          {nav.map(([label, path]) => <NavLink key={path} to={path} data-testid={`nav-${label.toLowerCase().replaceAll(" ", "-")}-link`}>{label}</NavLink>)}
        </nav>
        <Link to="/contact" className="header-cta" data-testid="header-consultation-link">Request a Consultation <ArrowUpRight size={15} /></Link>
        <button className="menu-button" onClick={() => setOpen(!open)} data-testid="mobile-menu-button" aria-expanded={open} aria-label="Toggle navigation">{open ? <X /> : <Menu />}</button>
      </div>
      <div className={`mobile-nav ${open ? "is-open" : ""}`} data-testid="mobile-navigation" aria-hidden={!open}>
        {nav.map(([label, path], index) => <NavLink key={path} to={path} onClick={() => setOpen(false)} style={{ "--i": index }} data-testid={`mobile-nav-${label.toLowerCase().replaceAll(" ", "-")}-link`}>{label}<span>0{index + 1}</span></NavLink>)}
        <Link to="/contact" onClick={() => setOpen(false)} className="button button--red" data-testid="mobile-consultation-link">Request a Consultation</Link>
      </div>
    </header>
  );
};

export const Footer = () => (
  <footer className="site-footer" data-testid="site-footer">
    <div className="footer-signal"><span>Long-term thinking</span><span>Independent evaluation</span><span>Disciplined perspective</span></div>
    <div className="footer-main shell">
      <div><Brand light /><p data-testid="footer-description">Thoughtful wealth management and research-led investment perspectives.</p></div>
      <div className="footer-links"><h3>Navigate</h3>{nav.slice(1).map(([label, path]) => <Link key={path} to={path} data-testid={`footer-${label.toLowerCase().replaceAll(" ", "-")}-link`}>{label}</Link>)}</div>
      <div className="footer-links"><h3>Legal</h3><Link to="/privacy-policy" data-testid="footer-privacy-link">Privacy Policy</Link><Link to="/terms-of-use" data-testid="footer-terms-link">Terms of Use</Link><Link to="/investment-disclaimer" data-testid="footer-disclaimer-link">Investment Disclaimer</Link><Link to="/risk-disclosures" data-testid="footer-risk-link">Risk Disclosures</Link><Link to="/sitemap" data-testid="footer-sitemap-link">Sitemap</Link></div>
      <div className="footer-contact"><h3>Contact</h3><p data-testid="footer-legal-entity">Baaz Managed Services Pvt Ltd</p><p data-testid="footer-office-address">B-119, ATS Bouquet, Sector 132, Noida, Uttar Pradesh 201304</p><a href="tel:+919818794449" data-testid="footer-phone-link">+91 98187 94449</a><a className="footer-whatsapp" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp Baaz Capital on +91 98187 94449" data-testid="footer-whatsapp-link"><MessageCircle size={17} /> WhatsApp +91 98187 94449</a><a href="mailto:himanshu@baazcapital.com" data-testid="footer-email-link">himanshu@baazcapital.com</a><Link to="/contact" data-testid="footer-private-consultation-link">Private consultation <ArrowUpRight size={16} /></Link></div>
    </div>
    <div className="footer-bottom shell"><span>© {new Date().getFullYear()} Baaz Capital</span><span>General information only. Capital is subject to risk.</span></div>
  </footer>
);

export const Layout = () => <><Header /><main><Outlet /></main><Footer /></>;